import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AumicoCoreProvider } from '@modeso/aumico-lib-core-fe';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { AumicoIntegrationsConfig, AumicoIntegrationsConfigService } from '../aumico-integrations.config';
import { IAbacusConnectBody } from '../models/integration/abacus-connect.model';
import { ITopalConnectBody } from '../models/integration/topal-connect.model';
import { IAccountoConnectBody } from '../models/integration/accounto-connect.model';
import { IProffixConnectBody } from '../models/integration/proffix-connect.model';
import { IAumicoCompanySummary } from '../models/integration/company-mapping-suggestion.model';
import {
    IAumicoCompanyWithMapping,
    IBulkUpsertMappingsBody,
    IBulkUpsertMappingsResponse,
    IConnectionMapping,
    IUpsertConnectionMappingBody,
} from '../models/integration/connection-mapping.model';
import { IAbacusImportBody } from '../models/integration/abacus-import.model';
import { ITopalImportBody } from '../models/integration/topal-import.model';
import { IAccountoImportBody } from '../models/integration/accounto-import.model';
import { IProffixImportBody } from '../models/integration/proffix-import.model';
import {
    ID365BcImportBody,
    IFiscalYear,
} from '../models/integration/d365bc-import.model';
import { IJournalImportResult } from '../models/integration/journal-import.model';
import { IExternalCompany } from '../models/integration/external-company.model';
import { IIntegrationConnection } from '../models/integration/integration-connection.model';
import { IntegrationVendor } from '../models/integration/integration-vendor.enum';
import {
    SelectedClosingFolder,
    SharepointConnection,
} from '../components/sharepoint-folders/sharepoint-folders.types';
// Type-only: the picker service imports IntegrationService (to mint per-resource tokens),
// so a value import here would create a runtime import cycle and break Angular DI.
import type { PickedSharepointFolder } from './sharepoint-file-picker.service';

/**
 * Lightweight Aumico Company response shape: what `GET /api/companies?onlyNameIdLegalForm=true`
 * returns. Mirrors `ICompanyNameIdLegalFormDTO` from `@modeso/types__aumico-lib-companies-be`
 * to avoid a hard import dependency.
 */
interface IAumicoCompanyLiteDTO {
    _id?: string;
    name: string;
    legalform?: string;
}

/**
 * Slim view of `GET /api/companies`: only the fields the auto matcher actually
 * needs. The full ICompanyDTO has many more, we type narrowly so unrelated BE
 * changes don't force FE recompiles.
 */
export interface IAumicoCompanyWithInlineMappingsDTO {
    _id?: string;
    name: string;
    integrations?: {
        connectionMappings?: Array<{
            vendor: string;
            connectionId: string;
            externalCompanyId: string;
            externalCompanyName: string;
            status: string;
            mappedAt: string;
        }>;
    };
}

export interface ITestExistingConnectionResult {
    ok: boolean;
    lastTestAt?: Date;
    errorCode?: string;
    errorMessage?: string;
}

@Injectable({ providedIn: 'root' })
export class IntegrationService {
    /** `/api/integrations`: vendor connection lifecycle (aumico-be). */
    private readonly integrationsEndpoint: string;
    /** `/api/companies`: Company scoped mapping CRUD (ms-companies). */
    private readonly companiesEndpoint: string;
    /** `/api/projects`: closing-project scoped SharePoint folder CRUD (REV-21679). */
    private readonly projectsEndpoint: string;

    public constructor(
        private readonly http: HttpClient,
        @Inject(AumicoIntegrationsConfigService) private readonly config: AumicoIntegrationsConfig,
        private readonly coreProvider: AumicoCoreProvider,
    ) {
        this.integrationsEndpoint = `${config.apiGateway}integrations`;
        this.companiesEndpoint = `${config.apiGateway}companies`;
        this.projectsEndpoint = `${config.apiGateway}projects`;
    }

    // ── Connect (D365 BC) ───────────────────────────────────────────────────
    //
    // The BE returns the Microsoft admin-consent URL. The FE opens it in a popup;
    // when consent succeeds the BE callback creates the IntegrationConnection
    // atomically and the popup postMessages a success/error event back to the opener.
    //
    // GET /api/integrations/d365bc/connect?environmentName=...[&displayName=...]
    //   → { authUrl }
    public getD365bcConnectUrl(
        environmentName: string,
        displayName?: string,
    ): Observable<{ authUrl: string }> {
        let params = new HttpParams().set('environmentName', environmentName);
        if (displayName != null && displayName.trim().length > 0) {
            params = params.set('displayName', displayName.trim());
        }
        this.coreProvider.dispatchBlockingRequest('integrationGetD365bcConnectUrl');
        return this.http
            .get<{ authUrl: string }>(`${this.integrationsEndpoint}/d365bc/connect`, { params })
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('integrationGetD365bcConnectUrl'),
                ),
            );
    }

    // ── Connect (Abacus) ────────────────────────────────────────────────────
    //
    // Abacus has no Microsoft style consent popup. The customer enters server URL
    // + OAuth2 client_credentials directly in the FE form; we POST them through
    // the generic create endpoint and the BE runs discovery → token → smoke test
    // → persist atomically. The vendor specific fields go inside `vendorInput`,
    // which the BE's `AbacusAuthAdapter.testCredentials` then reads.
    //
    // POST /api/integrations/abacus
    //   body:  { displayName, vendorInput: { serverUrl, clientId, clientSecret } }
    //   200 → IIntegrationConnection                  (generic create returns the connection directly)
    //   4xx → { errorCode, errorMessage }            (surfaced via the effect's error mapping)
    public connectAbacus(body: IAbacusConnectBody): Observable<IIntegrationConnection> {
        this.coreProvider.dispatchBlockingRequest('integrationConnectAbacus');
        return this.http
            .post<IIntegrationConnection>(
                `${this.integrationsEndpoint}/abacus`,
                {
                    displayName: body.displayName,
                    vendorInput: {
                        serverUrl: body.serverUrl,
                        clientId: body.clientId,
                        clientSecret: body.clientSecret,
                    },
                },
            )
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('integrationConnectAbacus'),
                ),
            );
    }

    // ── Connect (Topal) ─────────────────────────────────────────────────────
    //
    // Topal's REST API is on-premise (no consent popup). The customer enters the
    // server URL + username + password directly in the FE form; we POST them through
    // the generic create endpoint and the BE mints a password-grant token + runs a
    // smoke test → persist atomically. The vendor specific fields go inside
    // `vendorInput`, which the BE's `TopalAuthAdapter.testCredentials` then reads.
    //
    // POST /api/integrations/topal
    //   body:  { displayName, vendorInput: { serverUrl, username, password } }
    //   200 → IIntegrationConnection                  (generic create returns the connection directly)
    //   4xx → { errorCode, errorMessage }            (surfaced via the effect's error mapping)
    public connectTopal(body: ITopalConnectBody): Observable<IIntegrationConnection> {
        this.coreProvider.dispatchBlockingRequest('integrationConnectTopal');
        return this.http
            .post<IIntegrationConnection>(
                `${this.integrationsEndpoint}/topal`,
                {
                    displayName: body.displayName,
                    vendorInput: {
                        serverUrl: body.serverUrl,
                        username: body.username,
                        password: body.password,
                    },
                },
            )
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('integrationConnectTopal'),
                ),
            );
    }

    // ── Connect (Accounto) ──────────────────────────────────────────────────
    //
    // Accounto is a cloud API (no consent popup, no server URL — the BE's config holds
    // the address). The customer pastes one API key; we POST it through the generic
    // create endpoint and the BE reads the tenant and expiry from the key, checks it
    // against Accounto and persists atomically. The key never comes back.
    //
    // POST /api/integrations/accounto
    //   body:  { displayName, vendorInput: { apiKey } }
    //   201 → IIntegrationConnection                  (generic create returns the connection directly)
    //   4xx → { errorCode, errorMessage }            (surfaced via the effect's error mapping)
    public connectAccounto(body: IAccountoConnectBody): Observable<IIntegrationConnection> {
        this.coreProvider.dispatchBlockingRequest('integrationConnectAccounto');
        return this.http
            .post<IIntegrationConnection>(
                `${this.integrationsEndpoint}/accounto`,
                {
                    displayName: body.displayName,
                    vendorInput: {
                        apiKey: body.apiKey,
                    },
                },
            )
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('integrationConnectAccounto'),
                ),
            );
    }

    // ── Connect (Proffix) ───────────────────────────────────────────────────
    //
    // The PxRestAPI runs on the customer's own server (no consent popup). The customer
    // enters the server URL + REST-API password + username + password directly in the
    // FE form; we POST them through the generic create endpoint and the BE checks the
    // REST-API password, logs in once against the first Mandant (module FIB), reads the
    // Fibu master data and logs out → persist atomically. No Proffix session survives the
    // call: every later sync opens and closes its own session.
    //
    // POST /api/integrations/proffix
    //   body:  { displayName, vendorInput: { serverUrl, restApiKey, username, password } }
    //   201 → IIntegrationConnection                  (generic create returns the connection directly)
    //   4xx → { errorCode, errorMessage }            (surfaced via the effect's error mapping)
    public connectProffix(body: IProffixConnectBody): Observable<IIntegrationConnection> {
        this.coreProvider.dispatchBlockingRequest('integrationConnectProffix');
        return this.http
            .post<IIntegrationConnection>(
                `${this.integrationsEndpoint}/proffix`,
                {
                    displayName: body.displayName,
                    vendorInput: {
                        serverUrl: body.serverUrl,
                        restApiKey: body.restApiKey,
                        username: body.username,
                        password: body.password,
                    },
                },
            )
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('integrationConnectProffix'),
                ),
            );
    }

    // ── Connections ─────────────────────────────────────────────────────────

    public listConnections(): Observable<IIntegrationConnection[]> {
        this.coreProvider.dispatchBlockingRequest('integrationListConnections');
        return this.http
            .get<{ connections: IIntegrationConnection[] } | IIntegrationConnection[]>(
                this.integrationsEndpoint,
            )
            .pipe(
                map((response) => {
                    // Accept either { connections: [...] } (canonical) OR a bare array
                    // (defensive: future BE could drop the envelope).
                    if (Array.isArray(response)) return response;
                    return response?.connections ?? [];
                }),
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('integrationListConnections'),
                ),
            );
    }

    public testExistingConnection(connectionId: string): Observable<ITestExistingConnectionResult> {
        this.coreProvider.dispatchBlockingRequest('integrationTestExisting');
        return this.http
            .post<ITestExistingConnectionResult>(
                `${this.integrationsEndpoint}/${connectionId}/test`,
                {},
            )
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('integrationTestExisting'),
                ),
            );
    }

    public disconnect(connectionId: string): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('integrationDisconnect');
        return this.http
            .delete<void>(`${this.integrationsEndpoint}/${connectionId}`)
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('integrationDisconnect'),
                ),
            );
    }

    // ── External companies (vendor side) ───────────────────────────────────
    //
    // GET /api/integrations/:connectionId/external-companies → { companies: [...] }
    //
    // Server side this proxies to the vendor's `/companies` endpoint, transparently
    // refreshing the OAuth token. A 500 here typically means the BC permissions
    // are missing (admin consent done but the Aumico Client ID not yet enabled
    // inside BC's "Microsoft Entra Applications" page).
    //
    // No global blocking spinner: the reducer maintains `mapping.isLoading` and
    // the consumer (manage modal / wizard) renders a local spinner. A blocking
    // dispatch here would freeze the whole app if the BE / nginx ever hangs
    // the request (loadExternalCompanies is run on every tenant select).
    public listExternalCompanies(connectionId: string): Observable<IExternalCompany[]> {
        return this.http
            .get<{ companies: IExternalCompany[] }>(
                `${this.integrationsEndpoint}/${connectionId}/external-companies`,
            )
            .pipe(map((response) => response?.companies ?? []));
    }

    // ── Connection mappings (Company scoped, ms-companies) ─────────────────
    //
    // Mappings live on the Company document under `integrations.connectionMappings`.
    // The endpoints below are owned by ms-companies but logically belong to the
    // integration flow: the FE reads/writes them via this service so the per
    // Company logic doesn't have to know about the mapping wire format.

    /**
     * Upsert the mapping for `(companyId, vendor)`. Replaces any existing mapping
     * for the same pair. Server fills `status = 'ACTIVE'` and `mappedAt = now()`.
     *
     * `PUT /api/companies/:id/integrations/mappings`
     */
    public upsertMapping(
        companyId: string,
        body: IUpsertConnectionMappingBody,
    ): Observable<IConnectionMapping> {
        this.coreProvider.dispatchBlockingRequest('integrationUpsertMapping');
        return this.http
            .put<{ mapping: IConnectionMapping }>(
                `${this.companiesEndpoint}/${companyId}/integrations/mappings`,
                body,
            )
            .pipe(
                map((response) => response.mapping),
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('integrationUpsertMapping'),
                ),
            );
    }

    /**
     * Delete the mapping for `(companyId, vendor)`. Idempotent: 204 even if no
     * mapping exists.
     *
     * `DELETE /api/companies/:id/integrations/mappings?vendor=D365_BC`
     */
    public deleteMapping(companyId: string, vendor: IntegrationVendor): Observable<void> {
        const params = new HttpParams().set('vendor', vendor);
        this.coreProvider.dispatchBlockingRequest('integrationDeleteMapping');
        return this.http
            .delete<void>(`${this.companiesEndpoint}/${companyId}/integrations/mappings`, {
                params,
            })
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('integrationDeleteMapping'),
                ),
            );
    }

    /**
     * Lightweight Aumico Company catalog for the mapping wizard's picker.
     * Fetches the `name + id + legalform` projection so the dropdown can filter
     * client side without pulling full Company documents (which can be heavy).
     *
     * `GET /api/companies?onlyNameIdLegalForm=true`
     *
     * Per the BE doc D1 step 6 the picker uses this endpoint as is: there is
     * no `?q=` server side filter today, so the wizard caches the response and
     * filters in memory.
     *
     * DELIBERATELY does NOT dispatch a blocking request: the dropdown is an
     * inline async surface (the wizard owns its own `searchIsLoading` flag).
     * Using the global blocking overlay here would freeze the entire app on
     * the spinner while the catalog loads: see the Zefix service for the
     * same pattern (no blocking for live dropdowns).
     */
    public listAumicoCompanyOptions(): Observable<IAumicoCompanySummary[]> {
        const params = new HttpParams().set('onlyNameIdLegalForm', 'true');
        return this.http
            .get<IAumicoCompanyLiteDTO[]>(this.companiesEndpoint, { params })
            .pipe(
                map((response) =>
                    (response ?? [])
                        .filter((c) => c._id != null && c.name != null)
                        .map((c) => ({
                            id: c._id as string,
                            name: c.name,
                        })),
                ),
            );
    }

    /**
     * Full Aumico Company list with inline `integrations.connectionMappings`.
     * Used by the auto matcher to derive the two "already mapped" sets
     * (per connection + globally per vendor).
     *
     * The matcher only needs `_id`, `name`, and the mappings array: the rest
     * of the Company DTO is ignored. We accept the bandwidth cost here over
     * adding a dedicated lightweight endpoint because the call runs at most
     * once per Connect (auto match is one shot per spec).
     *
     * `GET /api/companies`
     */
    public listAumicoCompaniesWithInlineMappings(): Observable<
        IAumicoCompanyWithInlineMappingsDTO[]
    > {
        return this.http.get<IAumicoCompanyWithInlineMappingsDTO[]>(this.companiesEndpoint);
    }

    // ── D365 BC journal import ────────────────────────────────────────────
    //
    // The two endpoints below drive the "Daten synchronisieren" card in the
    // bilance sheet import page. Bexio parity: fiscal years are loaded eagerly
    // when the active company has a D365 BC mapping; on sync click the FE
    // auto matches the right fiscal year against the AR's balance sheet date.

    /**
     * GET /api/integrations/d365bc/fiscal-years?connectionId=...&externalCompanyId=...
     *
     * No global blocking spinner: the card's local loading state is enough.
     */
    public listD365BcFiscalYears(
        connectionId: string,
        externalCompanyId: string,
    ): Observable<IFiscalYear[]> {
        const params = new HttpParams()
            .set('connectionId', connectionId)
            .set('externalCompanyId', externalCompanyId);
        return this.http
            .get<{ fiscalYears: IFiscalYear[] }>(
                `${this.integrationsEndpoint}/d365bc/fiscal-years`,
                { params },
            )
            .pipe(map((res) => res.fiscalYears ?? []));
    }

    /**
     * POST /api/integrations/d365bc/import
     *
     * Returns the imported accounts (current + optional previous year) plus
     * balance validation. The validation result is informational only — even
     * when `balanced === false` the import still returns the data, the FE
     * just renders a warning in the summary modal.
     */
    public importD365BcJournal(body: ID365BcImportBody): Observable<IJournalImportResult> {
        return this.http.post<IJournalImportResult>(
            `${this.integrationsEndpoint}/d365bc/import`,
            body,
        );
    }

    /**
     * POST /api/integrations/abacus/import
     *
     * Same vendor-agnostic result as D365 BC. Abacus has no fiscal-year
     * discovery endpoint, so the caller is responsible for supplying the
     * dates (they come from the active Annual Report's General Info).
     */
    public importAbacusJournal(body: IAbacusImportBody): Observable<IJournalImportResult> {
        return this.http.post<IJournalImportResult>(
            `${this.integrationsEndpoint}/abacus/import`,
            body,
        );
    }

    /**
     * POST /api/integrations/topal/import
     *
     * Same vendor-agnostic result as D365 BC / Abacus. Topal has no fiscal-year
     * discovery endpoint, so the caller supplies the dates (from the active Annual
     * Report's General Info); the BE resolves the Topal fiscalYearId from them.
     */
    public importTopalJournal(body: ITopalImportBody): Observable<IJournalImportResult> {
        return this.http.post<IJournalImportResult>(
            `${this.integrationsEndpoint}/topal/import`,
            body,
        );
    }

    /**
     * POST /api/integrations/accounto/import
     *
     * Same vendor-agnostic result as D365 BC / Abacus / Topal. Accounto has no fiscal-year
     * endpoint, so the caller supplies the dates (from the active Annual Report's General
     * Info); the BE reads the postings and bookings of exactly that range.
     */
    public importAccountoJournal(body: IAccountoImportBody): Observable<IJournalImportResult> {
        return this.http.post<IJournalImportResult>(
            `${this.integrationsEndpoint}/accounto/import`,
            body,
        );
    }

    /**
     * POST /api/integrations/proffix/import
     *
     * Same vendor-agnostic result as D365 BC / Abacus / Topal. Proffix has no fiscal-year
     * list endpoint, so the caller supplies the dates (from the active Annual Report's
     * General Info); the BE uses them directly as `Buchungsdatum` filter bounds.
     */
    public importProffixJournal(body: IProffixImportBody): Observable<IJournalImportResult> {
        return this.http.post<IJournalImportResult>(
            `${this.integrationsEndpoint}/proffix/import`,
            body,
        );
    }

    /**
     * Bulk upsert mappings for a connection. Used by the post Connect auto match
     * flow: the matcher emits a `toCreate[]` array, we POST it in one shot.
     *
     * Cap is 200 per call (server enforces with `BATCH_TOO_LARGE`). Larger
     * batches must be chunked by the caller (helper not built in V1 because
     * orgs with > 200 Aumico Companies are vanishingly rare).
     *
     * Partial success is normal: the response contains a per item `results[]`
     * with `status: 'ok' | 'error'`. The matcher should never produce dupes
     * inside the same batch, but if it ever does the server returns 400 with
     * a whole batch error code (`DUPLICATE_COMPANY_ID_IN_BATCH` etc).
     *
     * No global blocking spinner: the manage modal's auto match banner owns
     * the loading UI.
     *
     * `POST /api/companies/connection-mappings/bulk-upsert`
     */
    public bulkUpsertMappings(
        body: IBulkUpsertMappingsBody,
    ): Observable<IBulkUpsertMappingsResponse> {
        return this.http.post<IBulkUpsertMappingsResponse>(
            `${this.companiesEndpoint}/connection-mappings/bulk-upsert`,
            body,
        );
    }

    /**
     * Aumico Companies mapped to a given vendor connection, each annotated with
     * its mapping payload. Used by the per connection card on the integrations
     * overview ("3 Aumico Companies linked to this connection").
     *
     * `GET /api/companies/by-integration-connection/:connectionId`
     *
     * Same rationale as `listExternalCompanies`: inline data load, local
     * `tenantDetail.isLoading` flag in the reducer drives the UI spinner. The
     * global blocking overlay must NOT be used here: if the endpoint is not
     * yet routed in nginx (early staging), a hung request would lock the app.
     */
    public getCompaniesByConnection(
        connectionId: string,
    ): Observable<IAumicoCompanyWithMapping[]> {
        return this.http
            .get<{ companies: IAumicoCompanyWithMapping[] }>(
                `${this.companiesEndpoint}/by-integration-connection/${connectionId}`,
            )
            .pipe(map((response) => response?.companies ?? []));
    }

    // ── SharePoint / Microsoft 365 document source (REV-21679) ──────────────
    //
    // SharePoint is a document source (not an accounting vendor). Connect uses a
    // Microsoft admin-consent popup like D365 BC; folders + connection state are
    // scoped to a closing PROJECT (not a Company), so they live under
    // `/api/projects/:projectId/...`. The picker access token is BE-minted.

    /**
     * BE returns the Microsoft OAuth consent URL for the SharePoint document
     * source. The container opens it in a popup; the BE callback persists the
     * connection and posts back on BroadcastChannel('sharepoint-auth') — a fast path
     * only (the channel is origin-scoped and the popup is served by the BE origin),
     * so the effect additionally POLLS `getSharepointConnection` while the popup is
     * open. That poll is the reliable signal.
     *
     * Blocking spinner (mirrors `getD365bcConnectUrl`): this is a deliberate
     * user gesture and we want the UI locked until the popup opens.
     *
     * GET /api/integrations/sharepoint/connect?projectId=...&displayName=... → { authUrl } (both params optional)
     */
    public getSharepointConnectUrl(
        projectId?: string | null,
        displayName?: string | null,
    ): Observable<{ authUrl: string }> {
        // The BE's `SharePointConnectDto` carries ONLY an optional `displayName` — the
        // connection is minted from the CALLING USER's organization, so no projectId is
        // needed to create it. It is still sent when the closing-scoped flow supplies one
        // (harmless, and leaves that call site untouched); the Org Settings card omits it.
        let params = new HttpParams();
        if (projectId != null && projectId.length > 0) {
            params = params.set('projectId', projectId);
        }
        if (displayName != null && displayName.length > 0) {
            params = params.set('displayName', displayName);
        }
        this.coreProvider.dispatchBlockingRequest('integrationGetSharepointConnectUrl');
        return this.http
            .get<{ authUrl: string }>(`${this.integrationsEndpoint}/sharepoint/connect`, { params })
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd(
                        'integrationGetSharepointConnectUrl',
                    ),
                ),
            );
    }

    /**
     * Current SharePoint connection state for a closing project (status +
     * account + the BE `connectionId` the picker-token/folder writes need).
     *
     * No blocking spinner: the container renders the pure view's own connection
     * states from the slice; a hung request must not freeze the whole app.
     *
     * GET /api/projects/:projectId/sharepoint-connection
     */
    public getSharepointConnection(
        projectId: string,
    ): Observable<SharepointConnection & { connectionId?: string }> {
        return this.http.get<SharepointConnection & { connectionId?: string }>(
            `${this.projectsEndpoint}/${projectId}/sharepoint-connection`,
        );
    }

    /**
     * BE-minted access token for the Microsoft File Picker v8. NON-blocking: the
     * container fetches it inline right before opening the picker.
     *
     * `baseUrl` is the TENANT's SharePoint Online origin, discovered best-effort by the BE
     * (null when Graph could not tell). The picker cannot be opened without it.
     *
     * `resource` mints a token scoped to that resource instead of the Graph token — the
     * File Picker v8 issues an `authenticate` command per resource and does not accept
     * Graph tokens, so the picker seam calls this once per requested resource.
     *
     * GET /api/integrations/sharepoint/:connectionId/picker-token[?resource=…]
     *   → { token, expiresAt, baseUrl }
     */
    public getSharepointPickerToken(
        connectionId: string,
        resource?: string,
    ): Observable<{ token: string; expiresAt: string | Date; baseUrl?: string | null }> {
        return this.http.get<{ token: string; expiresAt: string | Date; baseUrl?: string | null }>(
            `${this.integrationsEndpoint}/sharepoint/${connectionId}/picker-token`,
            resource != null && resource.length > 0 ? { params: { resource } } : {},
        );
    }

    /**
     * List the closing folders already selected for a project.
     * NON-blocking (list-scoped local spinner in the slice).
     *
     * REV-21057 BUG 3: the BE returns a BARE ARRAY of bindings (the controller returns
     * `ClosingFolderBindingsService.listFolders(...)` directly — verified live). This used to
     * be typed as `{ folders }` and mapped through `response.folders`, which always yielded
     * `[]`, so the folder list was permanently empty in the UI.
     *
     * GET /api/projects/:projectId/sharepoint-folders → SelectedClosingFolder[]
     */
    public listSharepointFolders(projectId: string): Observable<SelectedClosingFolder[]> {
        return this.http
            .get<SelectedClosingFolder[]>(
                `${this.projectsEndpoint}/${projectId}/sharepoint-folders`,
            )
            .pipe(map((response) => toFolderArray(response)));
    }

    /**
     * Persist newly picked folders for a project. The BE resolves each raw
     * picker item into a `SelectedClosingFolder` (path, file count, account) and
     * returns the full updated list. NON-blocking.
     *
     * The BE DTO (`SaveSharepointFoldersDto`) REQUIRES `connectionId` at the top level
     * (a body without it is rejected with 400 "connectionId should not be empty"), and
     * each folder item is whitelisted down to exactly four properties. The global
     * ValidationPipe runs with `whitelist: true`, so the picker's extra `source` field
     * would be silently stripped — we drop it here instead so the payload is explicit.
     *
     * REV-21057 BUG 3: the response is a BARE ARRAY of the project's bindings, same as the
     * GET — not a `{ folders }` wrapper.
     *
     * POST /api/projects/:projectId/sharepoint-folders
     *   body { connectionId, folders: [{ driveId, folderItemId, folderPath, includeSubfolders }] }
     *   → SelectedClosingFolder[]
     */
    public addSharepointFolders(
        projectId: string,
        connectionId: string,
        body: PickedSharepointFolder[],
    ): Observable<SelectedClosingFolder[]> {
        const folders = (body ?? []).map((folder) => ({
            driveId: folder.driveId,
            folderItemId: folder.folderItemId,
            folderPath: folder.folderPath,
            includeSubfolders: folder.includeSubfolders,
        }));
        return this.http
            .post<SelectedClosingFolder[]>(
                `${this.projectsEndpoint}/${projectId}/sharepoint-folders`,
                { connectionId, folders },
            )
            .pipe(map((response) => toFolderArray(response)));
    }

    /**
     * Remove one selected folder from a project. NON-blocking; idempotent server side.
     *
     * DELETE /api/projects/:projectId/sharepoint-folders/:folderId
     */
    public removeSharepointFolder(projectId: string, folderId: string): Observable<void> {
        return this.http.delete<void>(
            `${this.projectsEndpoint}/${projectId}/sharepoint-folders/${folderId}`,
        );
    }

    /**
     * Flip 'include subfolders' on ONE already-saved folder (AC B3).
     *
     * PATCH and not another POST: saving dedups on drive + folder item server side, so
     * re-posting the same folder with a different flag is a silent no-op.
     *
     * PATCH /api/projects/:projectId/sharepoint-folders/:folderId
     */
    public updateSharepointFolder(
        projectId: string,
        folderId: string,
        includeSubfolders: boolean,
    ): Observable<SelectedClosingFolder> {
        return this.http.patch<SelectedClosingFolder>(
            `${this.projectsEndpoint}/${projectId}/sharepoint-folders/${folderId}`,
            { includeSubfolders },
        );
    }
}

/**
 * Normalise a SharePoint folders payload into an array (REV-21057 BUG 3).
 *
 * The BARE ARRAY is the real, primary contract — that is what the BE controller returns
 * today. A `{ folders: [...] }` wrapper is accepted defensively so a future BE change to a
 * wrapped envelope cannot silently empty the list again; anything else yields `[]`.
 */
function toFolderArray(response: unknown): SelectedClosingFolder[] {
    const raw = Array.isArray(response)
        ? response
        : (response as { folders?: unknown } | null)?.folders;
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw.map((item) => toSelectedClosingFolder(item as Record<string, unknown>));
}

/**
 * Map one persisted binding onto the pure view's `SelectedClosingFolder` (REV-21057 BUG 3).
 *
 * The BE serialises the `ClosingFolderBinding` model as-is, so the wire field is
 * `folderPath` while the view renders `path`; passing the raw binding straight through gave
 * rows with a blank path. `source` is not persisted (SharePoint is the only live surface)
 * and `account` is not part of the binding — the connected account is shown in the banner
 * instead. Already-mapped payloads pass through unchanged, so this stays correct if the BE
 * ever starts emitting the view shape directly.
 */
function toSelectedClosingFolder(item: Record<string, unknown>): SelectedClosingFolder {
    const path = typeof item['path'] === 'string' ? item['path'] : item['folderPath'];
    const id = item['id'] ?? item['_id'];
    return {
        ...(item as unknown as SelectedClosingFolder),
        id: typeof id === 'string' ? id : String(id ?? ''),
        path: typeof path === 'string' ? path : '',
        source: item['source'] === 'onedrive' ? 'onedrive' : 'sharepoint',
        includeSubfolders: item['includeSubfolders'] === true,
        account: typeof item['account'] === 'string' ? item['account'] : '',
    };
}
